/**
 * AdminESign — Documents hub for e-signature requests across the whole tenant.
 *
 * Shows every signature request sent (all parent types) with:
 *   - Status filter chips
 *   - Search by title / signer
 *   - Deep-link back to the parent record (Job / Sub-Contractor / Tenant)
 *   - Per-row Refresh / Remind / Cancel / Download actions
 *
 * Uses the reusable <ESignPanel/> for the ad-hoc composer at the top.
 */
import { useEffect, useMemo, useState } from "react";
import { AdminShell } from "@/components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  FileText, Sparkle, CheckCircle, Clock, Warning, Prohibit,
  PaperPlaneTilt, ArrowsClockwise, MagnifyingGlass,
} from "@phosphor-icons/react";
import ESignPanel from "@/components/ESignPanel";

const FILTERS = [
  { id: "",                 label: "All",       tone: "slate"   },
  { id: "sent",             label: "Awaiting",  tone: "cyan"    },
  { id: "viewed",           label: "Viewed",    tone: "sky"     },
  { id: "partially_signed", label: "Partial",   tone: "amber"   },
  { id: "signed",           label: "Signed",    tone: "emerald" },
  { id: "declined",         label: "Declined",  tone: "rose"    },
  { id: "canceled",         label: "Canceled",  tone: "slate"   },
];

const TONE = {
  slate:   "bg-slate-100 text-slate-800",
  cyan:    "bg-cyan-100 text-cyan-800",
  sky:     "bg-sky-100 text-sky-800",
  amber:   "bg-amber-100 text-amber-800",
  emerald: "bg-emerald-100 text-emerald-800",
  rose:    "bg-rose-100 text-rose-800",
};

const PARENT_LABEL = {
  work_order:     "Work Order",
  tenant:         "Tenant",
  sub_contractor: "Sub-Contractor",
  landlord:       "Landlord",
  lease:          "Lease",
  estimate:       "Estimate",
  inspection:     "Inspection",
};

const parentLink = (p) => {
  if (!p) return null;
  if (p.type === "work_order")     return `/admin/jobs/${p.id}`;
  if (p.type === "sub_contractor") return `/admin/sub-contractors`;
  return null;
};

export default function AdminESign() {
  const [rows, setRows]       = useState([]);
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(true);
  const [status, setStatus]   = useState("");
  const [q, setQ]             = useState("");
  const [config, setConfig]   = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/esign/requests", {
        params: { status: status || undefined, page_size: 100 },
      });
      setRows(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      setRows([]);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    api.get("/esign/config").then(r => setConfig(r.data)).catch(() => setConfig({ configured: false }));
  }, []);
  useEffect(() => { load(); }, [status]); // eslint-disable-line

  const filtered = useMemo(() => {
    if (!q.trim()) return rows;
    const s = q.toLowerCase();
    return rows.filter(r =>
      (r.title || "").toLowerCase().includes(s) ||
      (r.subject || "").toLowerCase().includes(s) ||
      (r.signers || []).some(x => (x.name || "").toLowerCase().includes(s) || (x.email_address || "").toLowerCase().includes(s))
    );
  }, [rows, q]);

  const counts = useMemo(() => {
    const acc = { total: rows.length, signed: 0, awaiting: 0, declined: 0 };
    rows.forEach(r => {
      if (["signed", "downloadable", "completed"].includes(r.status)) acc.signed++;
      else if (["sent", "viewed", "partially_signed"].includes(r.status)) acc.awaiting++;
      else if (r.status === "declined") acc.declined++;
    });
    return acc;
  }, [rows]);

  return (
    <AdminShell>
      <div className="max-w-6xl mx-auto p-4 space-y-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-xl bg-gradient-to-br from-indigo-600 to-violet-700 text-white">
            <FileText size={26} weight="fill"/>
          </div>
          <div>
            <h1 className="text-2xl font-black text-slate-900">E-Sign Center</h1>
            <p className="text-sm text-slate-500">Dropbox Sign — every signature request across your portfolio.</p>
          </div>
          {config?.test_mode && (
            <span className="ml-auto px-2.5 py-1 rounded-full bg-amber-400 text-black text-[10px] font-bold uppercase">
              Test mode
            </span>
          )}
        </div>

        {!config?.configured && (
          <div className="rounded-2xl bg-amber-50 border-2 border-amber-300 p-4">
            <div className="flex items-start gap-2">
              <Warning size={22} className="text-amber-700 shrink-0 mt-0.5"/>
              <div>
                <div className="font-black text-amber-900">Add your Dropbox Sign API key to activate</div>
                <ol className="text-sm text-amber-900 list-decimal ml-5 mt-1 space-y-0.5">
                  <li>Go to <a className="underline font-bold" href="https://app.hellosign.com/home/myAccount#api" target="_blank" rel="noreferrer">app.hellosign.com → Account → API</a></li>
                  <li>Click <b>Create Key</b> (or copy your existing key)</li>
                  <li>Paste it into <code className="bg-amber-100 px-1 rounded">DROPBOX_SIGN_API_KEY</code> in <code className="bg-amber-100 px-1 rounded">backend/.env</code></li>
                  <li>Restart backend (Emergent auto-restarts on save). Reload this page.</li>
                </ol>
                <div className="mt-2 text-xs text-amber-800">
                  Any templates you create in Dropbox Sign will show up here automatically.
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <StatCard label="Total sent"  value={counts.total}    icon={PaperPlaneTilt} tone="slate"/>
          <StatCard label="Awaiting"    value={counts.awaiting} icon={Clock}          tone="amber"/>
          <StatCard label="Signed"      value={counts.signed}   icon={CheckCircle}    tone="emerald"/>
          <StatCard label="Declined"    value={counts.declined} icon={Prohibit}       tone="rose"/>
        </div>

        {/* Ad-hoc composer — always sends to a "documents" parent (untyped) */}
        <div>
          <div className="text-xs font-black text-slate-500 uppercase tracking-wider mb-2">
            <Sparkle size={12} className="inline -mt-0.5 mr-1 text-indigo-600"/> Quick send
          </div>
          <ESignPanel
            parentType="lease"
            parentId="ad_hoc"
            defaultSubject="Field Radar document for signature"
          />
        </div>

        {/* Filters + Search */}
        <div className="flex flex-wrap items-center gap-2">
          {FILTERS.map(f => (
            <button key={f.id || "all"} onClick={() => setStatus(f.id)}
              className={`px-3 py-1.5 rounded-full text-xs font-black ${TONE[f.tone]} ${status === f.id ? "ring-2 ring-indigo-500" : "opacity-70 hover:opacity-100"}`}
              data-testid={`esign-filter-${f.id || "all"}`}>
              {f.label}
            </button>
          ))}
          <div className="ml-auto flex items-center gap-2">
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400"/>
              <input value={q} onChange={e => setQ(e.target.value)}
                placeholder="Search title, signer…"
                className="pl-7 pr-2 py-1.5 rounded-lg border-2 border-slate-200 text-xs bg-white w-56"
                data-testid="esign-search"/>
            </div>
            <button onClick={load}
              className="rounded-lg bg-slate-800 hover:bg-slate-900 text-white text-xs font-bold px-3 py-1.5 flex items-center gap-1"
              data-testid="esign-reload">
              <ArrowsClockwise size={12}/> Reload
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="rounded-2xl border-2 border-slate-200 bg-white overflow-hidden">
          <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-slate-100 text-[10px] font-black text-slate-600 uppercase">
            <div className="col-span-4">Document</div>
            <div className="col-span-2">Parent</div>
            <div className="col-span-3">Signers</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-1 text-right">Sent</div>
          </div>
          {loading ? (
            <div className="p-6 text-center text-slate-400 text-sm">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-slate-400 text-sm">
              {config?.configured
                ? "No signature requests match this filter yet."
                : "Add your API key above to start sending documents."}
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {filtered.map(r => <ListRow key={r.signature_request_id} row={r} onChanged={load}/>)}
            </div>
          )}
        </div>
      </div>
    </AdminShell>
  );
}

function StatCard({ label, value, icon: Icon, tone }) {
  const t = TONE[tone] || TONE.slate;
  return (
    <div className={`rounded-xl ${t} p-3`}>
      <div className="flex items-center gap-2">
        <Icon size={18} weight="fill"/>
        <div className="text-[10px] font-black uppercase opacity-80">{label}</div>
      </div>
      <div className="text-2xl font-black mt-1">{value}</div>
    </div>
  );
}

function ListRow({ row, onChanged }) {
  const link = parentLink(row.parent);
  const isTerminal = ["signed", "downloadable", "completed", "canceled", "declined"].includes(row.status);
  const statusBg = {
    sent: "bg-cyan-100 text-cyan-800",
    viewed: "bg-sky-100 text-sky-800",
    partially_signed: "bg-amber-100 text-amber-800",
    signed: "bg-emerald-100 text-emerald-800",
    downloadable: "bg-emerald-100 text-emerald-800",
    completed: "bg-emerald-100 text-emerald-800",
    declined: "bg-rose-100 text-rose-800",
    canceled: "bg-slate-200 text-slate-700",
  }[row.status] || "bg-slate-100 text-slate-700";

  const act = async (path) => {
    try { await api.post(path); toast.success("Done"); onChanged(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Failed")); }
  };

  return (
    <div className="grid grid-cols-12 gap-2 px-3 py-2.5 text-sm items-center hover:bg-slate-50">
      <div className="col-span-4 min-w-0">
        <div className="font-bold text-slate-800 truncate">{row.title || row.subject || row.signature_request_id}</div>
        <div className="text-[10px] text-slate-500 truncate">{row.source} · {row.signature_request_id.slice(0, 12)}…</div>
      </div>
      <div className="col-span-2 text-xs">
        {link ? (
          <Link to={link} className="rounded-md bg-slate-100 hover:bg-slate-200 px-2 py-1 text-slate-700 font-bold">
            {PARENT_LABEL[row.parent?.type] || row.parent?.type || "—"}
          </Link>
        ) : (
          <span className="text-slate-500">{PARENT_LABEL[row.parent?.type] || row.parent?.type || "—"}</span>
        )}
      </div>
      <div className="col-span-3 text-xs text-slate-600 truncate">
        {(row.signers || []).map((s) => (
          <div key={s.email_address} className="truncate">
            <span className="font-bold">{s.name}</span>{" "}
            <span className="text-slate-400">{s.email_address}</span>
          </div>
        ))}
      </div>
      <div className="col-span-2 flex items-center gap-1 flex-wrap">
        <span className={`rounded-full px-2 py-0.5 text-[10px] font-black uppercase ${statusBg}`}>
          {row.status}
        </span>
        <button onClick={() => act(`/esign/requests/${row.signature_request_id}/refresh`)}
          className="rounded-md bg-white hover:bg-slate-100 border border-slate-200 px-1.5 py-0.5 text-[10px] font-bold text-slate-600"
          data-testid={`esign-list-refresh-${row.signature_request_id}`}>
          <ArrowsClockwise size={10} className="inline"/> Sync
        </button>
        {!isTerminal && (
          <button onClick={() => act(`/esign/requests/${row.signature_request_id}/cancel`)}
            className="rounded-md bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 px-1.5 py-0.5 text-[10px] font-bold"
            data-testid={`esign-list-cancel-${row.signature_request_id}`}>
            Cancel
          </button>
        )}
      </div>
      <div className="col-span-1 text-right text-[10px] text-slate-400">
        {row.created_at ? new Date(row.created_at).toLocaleDateString() : ""}
      </div>
    </div>
  );
}
